/**
 * Pure 5x5-subgrid surface / walkability contract.
 *
 * COL-004C owns walkability. COL-004D may import this module for visible
 * path/floor presentation only. Editor, SaveData/ParkState, saveVersion,
 * pathfinding and visitor patrol must not import it.
 */

import {
  FOUNDATION_DIVISION,
  LEGACY_CELL_SIZE,
  createSubgridSpec,
  legacyCellToSubgridOrigin,
  type GridCoord,
  type SubgridCoord,
} from './subgrid';

export const SURFACE_DIVISION = FOUNDATION_DIVISION;
export const SURFACE_CELL_SIZE = LEGACY_CELL_SIZE / SURFACE_DIVISION;

const FOUNDATION_SPEC = createSubgridSpec(SURFACE_DIVISION);

export const DEFAULT_SURFACE = 'grass' as const;
export const WALKABLE_SURFACE = 'path' as const;
export type SurfaceKind = typeof DEFAULT_SURFACE | typeof WALKABLE_SURFACE;

/**
 * Persistence-unused optional id for a later floor-painting task.
 * Not a SaveData field and not consumed by runtime.
 */
export type FloorMaterialId = string;

export interface SurfaceMembership {
  coord: SubgridCoord;
  surface: SurfaceKind;
  walkable: boolean;
  floorMaterial?: FloorMaterialId;
}

export const CARDINAL_OFFSETS: readonly SubgridCoord[] = [
  { sx: 1, sz: 0 },
  { sx: -1, sz: 0 },
  { sx: 0, sz: 1 },
  { sx: 0, sz: -1 },
];

const SUBCELL_KEY = /^(-?\d+),(-?\d+)$/;

export function subcellKey(coord: SubgridCoord): string {
  return `${coord.sx},${coord.sz}`;
}

export function parseSubcellKey(key: string): SubgridCoord {
  const match = SUBCELL_KEY.exec(key);
  if (!match) {
    throw new Error(`Invalid subcell key "${key}"`);
  }
  return { sx: Number(match[1]), sz: Number(match[2]) };
}

export function compareSubcells(a: SubgridCoord, b: SubgridCoord): number {
  return a.sx - b.sx || a.sz - b.sz;
}

export function sortSubcells(coords: readonly SubgridCoord[]): SubgridCoord[] {
  return [...coords].sort(compareSubcells);
}

export function walkableKeySet(cells: readonly SubgridCoord[]): Set<string> {
  return new Set(cells.map(subcellKey));
}

/** All logical 0.48 subcells inside one legacy 2.4 cell. */
export function legacyCellCoverage(col: number, row: number): SubgridCoord[] {
  const origin = legacyCellToSubgridOrigin(col, row, FOUNDATION_SPEC);
  const cells: SubgridCoord[] = [];
  for (let localX = 0; localX < SURFACE_DIVISION; localX += 1) {
    for (let localZ = 0; localZ < SURFACE_DIVISION; localZ += 1) {
      cells.push({ sx: origin.sx + localX, sz: origin.sz + localZ });
    }
  }
  return sortSubcells(cells);
}

/** Union of full 5x5 coverage for each legacy path/entrance cell. */
export function walkableCoverageFromLegacyCells(cells: readonly GridCoord[]): SubgridCoord[] {
  const seen = new Set<string>();
  const coverage: SubgridCoord[] = [];
  for (const cell of cells) {
    for (const subcell of legacyCellCoverage(cell.col, cell.row)) {
      const key = subcellKey(subcell);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      coverage.push(subcell);
    }
  }
  return sortSubcells(coverage);
}

export function containsSubcell(keys: ReadonlySet<string>, coord: SubgridCoord): boolean {
  return keys.has(subcellKey(coord));
}

export function isWalkable(coord: SubgridCoord, walkableKeys: ReadonlySet<string>): boolean {
  return containsSubcell(walkableKeys, coord);
}

export function surfaceMembership(
  coord: SubgridCoord,
  walkableKeys: ReadonlySet<string>,
  floorMaterial?: FloorMaterialId,
): SurfaceMembership {
  const walkable = isWalkable(coord, walkableKeys);
  const membership: SurfaceMembership = {
    coord,
    surface: walkable ? WALKABLE_SURFACE : DEFAULT_SURFACE,
    walkable,
  };
  if (floorMaterial !== undefined) {
    membership.floorMaterial = floorMaterial;
  }
  return membership;
}

export function cardinalNeighbors(coord: SubgridCoord): SubgridCoord[] {
  return CARDINAL_OFFSETS.map((offset) => ({
    sx: coord.sx + offset.sx,
    sz: coord.sz + offset.sz,
  }));
}

export function areCardinallyAdjacent(a: SubgridCoord, b: SubgridCoord): boolean {
  return Math.abs(a.sx - b.sx) + Math.abs(a.sz - b.sz) === 1;
}

/** Cardinal flood-fill of walkable keys starting at `start`. Not a pathfinder. */
export function connectedWalkableFrom(
  start: SubgridCoord,
  walkableKeys: ReadonlySet<string>,
): SubgridCoord[] {
  const startKey = subcellKey(start);
  if (!walkableKeys.has(startKey)) {
    return [];
  }
  const seen = new Set<string>([startKey]);
  const queue: SubgridCoord[] = [start];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const neighbor of cardinalNeighbors(current)) {
      const key = subcellKey(neighbor);
      if (!walkableKeys.has(key) || seen.has(key)) {
        continue;
      }
      seen.add(key);
      queue.push(neighbor);
    }
  }
  return sortSubcells([...seen].map(parseSubcellKey));
}

export function isCardinallyConnectedFrom(
  start: SubgridCoord,
  walkableKeys: ReadonlySet<string>,
): boolean {
  if (!walkableKeys.has(subcellKey(start))) {
    return false;
  }
  return connectedWalkableFrom(start, walkableKeys).length === walkableKeys.size;
}

/** Centered paving band inside one legacy path cell. 3 subcells = 1.44 world units. */
export const PATH_BAND_WIDTH_SUBCELLS = 3;
export const PATH_BAND_WORLD_WIDTH = PATH_BAND_WIDTH_SUBCELLS * SURFACE_CELL_SIZE;

export type PathFloorRole = 'entrance' | 'path';

export interface VisiblePathFloorCell {
  coord: SubgridCoord;
  role: PathFloorRole;
}

/**
 * Middle `widthSubcells` of a north-south legacy cell (canonical central path).
 * Local X is trimmed; local Z keeps the full 5-subcell run so cells stay connected.
 */
export function centeredPathBandCoverage(
  col: number,
  row: number,
  widthSubcells = PATH_BAND_WIDTH_SUBCELLS,
): SubgridCoord[] {
  if (!Number.isInteger(widthSubcells) || widthSubcells < 1 || widthSubcells > SURFACE_DIVISION) {
    throw new Error(`Path band width must be an integer in 1..${SURFACE_DIVISION}, got ${widthSubcells}`);
  }
  const origin = legacyCellToSubgridOrigin(col, row, FOUNDATION_SPEC);
  const inset = Math.floor((SURFACE_DIVISION - widthSubcells) / 2);
  const cells: SubgridCoord[] = [];
  for (let localX = inset; localX < inset + widthSubcells; localX += 1) {
    for (let localZ = 0; localZ < SURFACE_DIVISION; localZ += 1) {
      cells.push({ sx: origin.sx + localX, sz: origin.sz + localZ });
    }
  }
  return sortSubcells(cells);
}

/** Entrance keeps full 5x5 paving; each path cell contributes only the centered 3-wide band. */
export function visiblePathFloorCoverage(
  entrance: GridCoord,
  pathCells: readonly GridCoord[],
): VisiblePathFloorCell[] {
  const seen = new Set<string>();
  const cells: VisiblePathFloorCell[] = [];
  for (const coord of legacyCellCoverage(entrance.col, entrance.row)) {
    seen.add(subcellKey(coord));
    cells.push({ coord, role: 'entrance' });
  }
  for (const path of pathCells) {
    for (const coord of centeredPathBandCoverage(path.col, path.row)) {
      const key = subcellKey(coord);
      if (seen.has(key)) {
        continue;
      }
      seen.add(key);
      cells.push({ coord, role: 'path' });
    }
  }
  return cells.sort((a, b) => compareSubcells(a.coord, b.coord));
}
